import { getTeamIcon } from '@/utils/teamIcons';

interface Team {
  id: string;
  name: string;
  iconName: string;
  iconColor: string;
  totalScore: number;
  challengesCompleted?: number;
}

type PositionChange = 'up' | 'down' | 'none';

interface DisplayLeaderboardRowProps {
  team: Team;
  rank: number;
  pointsLabel: string;
  /** Zebra striping index within the list container. */
  striped?: boolean;
  positionChange?: PositionChange;
}

/**
 * Ranks 4+ as rows of the deck's table treatment: mono rank column,
 * zebra striping, serif tabular points.
 */
export function DisplayLeaderboardRow({
  team,
  rank,
  pointsLabel,
  striped = false,
  positionChange,
}: DisplayLeaderboardRowProps) {
  const TeamIcon = getTeamIcon(team.iconName);
  const hasMoved = positionChange && positionChange !== 'none';

  return (
    <div
      className="dsp-animated flex items-center gap-4 px-5 py-3"
      style={{
        backgroundColor: striped ? 'color-mix(in srgb, var(--dsp-text) 3%, transparent)' : undefined,
        animation: hasMoved ? 'slowBlink 1.5s ease-in-out' : undefined,
      }}
    >
      <span
        className="w-8 flex-shrink-0 text-center font-mono text-sm tabular-nums"
        style={{ color: 'var(--dsp-soft)' }}
      >
        {String(rank).padStart(2, '0')}
      </span>

      <div
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: team.iconColor }}
      >
        <TeamIcon className="h-4.5 w-4.5 text-white" />
      </div>

      <div className="min-w-0 flex-1">
        <span className="block truncate font-medium">{team.name}</span>
        {team.challengesCompleted !== undefined && (
          <span
            className="font-mono text-[0.625rem] uppercase tracking-[0.15em]"
            style={{ color: 'var(--dsp-soft)' }}
          >
            {team.challengesCompleted} challenges
          </span>
        )}
      </div>

      <div className="flex flex-shrink-0 items-baseline gap-1.5">
        <span className="text-xl font-semibold tabular-nums">
          {team.totalScore.toLocaleString()}
        </span>
        <span
          className="font-mono text-[0.625rem] uppercase tracking-[0.15em]"
          style={{ color: 'var(--dsp-soft)' }}
        >
          {pointsLabel}
        </span>
      </div>
    </div>
  );
}
